import {Stars} from '@commercial-helios/ui/src/components/stars'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import MediaList from '@/components/media-list'
import SEO from '@/components/seo'
import SimpleSignupForm from '@/components/simple-signup-form'
import type {FetchQuery} from '@/graphql-types'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithFeatures from '@/sections/title-with-features'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import RichText from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import {Layout, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'

import * as s from './fetch.styles'

type Props = PageProps<FetchQuery>

const Fetch = (props: Props) => {
    const data = props.data.allPrismicFetchPage.nodes[0].data

    if (!data) {
        return handleNoPrismicData()
    }

    const formId = getEnvironmentFormId(
        data.signup_banner.document.data.hubspot_form_id_production,
        data.signup_banner.document.data.hubspot_form_id_staging
    )

    return (
        <>
            <Header />

            <Main>
                <s.HeroSection padding="small">
                    <FeatureBlock
                        image={data.hero_image}
                        preTitle={data.pre_title}
                        title={data.title.richText}
                        videoId={data.video_id}
                    >
                        <SimpleSignupForm
                            {...data.signup_banner.document.data}
                            withBorder
                            formId={formId}
                            placeholder={data.signup_banner.document.data.email_input_placeholder}
                            submitText={data.signup_banner.document.data.submit_text}
                            errorText={data.signup_banner.document.data.email_input_error_text}
                        />
                    </FeatureBlock>
                </s.HeroSection>

                <TitleWithFeatures
                    title={data.features_title}
                    backgroundColor="pink"
                    items={data.features.map((item) => ({
                        ...item,
                        body: item.body.richText,
                    }))}
                />

                <Layout as={Section} variant="two-asymmetrical">
                    <Text variant="heading-large">{data.how_to_title}</Text>

                    <MediaList items={data.how_to_steps} />
                </Layout>

                <TitleWithFeatures
                    title={data.questions_title}
                    backgroundColor="muted"
                    items={data.questions.map((item) => ({
                        ...item,
                        bodyExtra: item.body_2.richText,
                        body: item.body.richText,
                    }))}
                />

                <s.ThreeColSection>
                    <Layout variant="two-asymmetrical">
                        <Text variant="heading-large">{data.testimonials_title}</Text>
                    </Layout>

                    <Layout variant="three-equal">
                        {data.testimonials.map((testimonial, i) => (
                            <s.ThreeColSectionItem
                                hideOnMobile={i >= 3}
                                key={`${testimonial.quote}-${i}`}
                            >
                                <s.TestimonialStars>
                                    <Stars numberOfStars={Number(testimonial.stars)} />

                                    <Text>{`"${testimonial.quote}"`}</Text>
                                </s.TestimonialStars>

                                <s.TestimonialAttribution>
                                    <RichText
                                        variant="paragraph-base"
                                        color="lightMuted"
                                        content={testimonial.attribution.richText}
                                    />
                                </s.TestimonialAttribution>
                            </s.ThreeColSectionItem>
                        ))}
                    </Layout>
                </s.ThreeColSection>

                {data.signup_banner && (
                    <SignupBannerSection
                        {...data.signup_banner.document.data}
                        color="pink"
                        formId={formId}
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<FetchQuery>) => {
    const pageData = data.allPrismicFetchPage.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query Fetch($locale: String!) {
        allPrismicFetchPage(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    pre_title
                    title {
                        richText
                    }
                    hero_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    video_id
                    features_title
                    features {
                        title
                        body {
                            richText
                        }
                    }
                    how_to_title
                    how_to_steps {
                        title
                    }
                    questions_title
                    questions {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            richText
                        }
                        body_2 {
                            richText
                        }
                    }
                    testimonials_title
                    testimonials {
                        stars
                        quote
                        attribution {
                            richText
                        }
                    }
                    signup_banner {
                        document {
                            ...SignupBanner
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(Fetch)
